import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  CheckCircleIcon,
  XCircleIcon,
  CreditCardIcon,
  ArrowUpIcon,
  ArrowDownIcon,
} from '@heroicons/react/24/outline';
import { subscriptionApi } from '@services/api/subscription';
import toast from 'react-hot-toast';
import type { Subscription } from '@types';
import { formatEmployeeLimit } from '@/types/subscription';

interface CurrentPlanCardProps {
  subscription: Subscription;
  onUpgrade: (tier: string) => void;
  onCancelSuccess: () => void;
}

const statusColors = {
  ACTIVE: 'bg-green-100 text-green-800',
  CANCELLED: 'bg-gray-100 text-gray-800',
  EXPIRED: 'bg-red-100 text-red-800',
  SUSPENDED: 'bg-yellow-100 text-yellow-800',
};

const tierHierarchy = ['FREE', 'BASIC', 'PROFESSIONAL', 'ENTERPRISE'];

export default function CurrentPlanCard({
  subscription,
  onUpgrade,
  onCancelSuccess,
}: CurrentPlanCardProps) {
  const [cancelling, setCancelling] = useState(false);

  const handleCancel = async () => {
    if (
      !confirm(
        'Are you sure you want to cancel your subscription? This action cannot be undone.'
      )
    ) {
      return;
    }

    try {
      setCancelling(true);
      await subscriptionApi.cancelSubscription();
      toast.success('Subscription cancelled successfully');
      onCancelSuccess();
    } catch (error: any) {
      toast.error(error.message || 'Failed to cancel subscription');
    } finally {
      setCancelling(false);
    }
  };

  const canUpgrade = subscription.tier !== 'ENTERPRISE';
  const canDowngrade = subscription.tier !== 'FREE';
  const isActive = subscription.status === 'ACTIVE';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white overflow-hidden shadow rounded-lg"
    >
      <div className="px-4 py-5 sm:p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Current Plan
            </h3>
            <div className="mt-1 flex items-center gap-3">
              <p className="text-3xl font-bold text-indigo-600">
                {subscription.tier}
              </p>
              <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  statusColors[subscription.status]
                }`}
              >
                {subscription.status}
              </span>
            </div>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-500">
              {subscription.billing_period === 'MONTHLY' ? 'Monthly' : 'Yearly'}
            </p>
            <p className="text-2xl font-bold text-gray-900">
              ${subscription.price}
              <span className="text-sm font-normal text-gray-500">
                /{subscription.billing_period === 'MONTHLY' ? 'mo' : 'yr'}
              </span>
            </p>
          </div>
        </div>

        {/* Dates */}
        <div className="mt-6 grid grid-cols-2 gap-4">
          <div>
            <p className="text-sm font-medium text-gray-500">Started</p>
            <p className="mt-1 text-sm text-gray-900">
              {subscription.started_at
                ? new Date(subscription.started_at).toLocaleDateString()
                : 'N/A'}
            </p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">
              {subscription.status === 'CANCELLED' ? 'Cancelled' : 'Expires'}
            </p>
            <p className="mt-1 text-sm text-gray-900">
              {subscription.expires_at
                ? new Date(subscription.expires_at).toLocaleDateString()
                : 'Never'}
            </p>
          </div>
        </div>

        {/* Features */}
        <div className="mt-6">
          <h4 className="text-sm font-medium text-gray-900 mb-3">
            Plan Features
          </h4>
          <ul className="space-y-2">
            {subscription.features.map((feature, index) => (
              <li key={index} className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                <span className="text-sm text-gray-700">{feature}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Employee Limit */}
        <div className="mt-6 p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">
              Employee Limit
            </span>
            <span className="text-sm font-bold text-gray-900">
              {formatEmployeeLimit(subscription.employee_limit)}
            </span>
          </div>
        </div>

        {/* Actions */}
        <div className="mt-6 flex flex-wrap gap-3">
          {isActive && canUpgrade && (
            <button
              onClick={() =>
                onUpgrade(
                  tierHierarchy[tierHierarchy.indexOf(subscription.tier) + 1]!
                )
              }
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <ArrowUpIcon className="h-4 w-4 mr-2" />
              Upgrade Plan
            </button>
          )}

          {isActive && canDowngrade && subscription.tier !== 'FREE' && (
            <button
              onClick={() => onUpgrade('FREE')}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <ArrowDownIcon className="h-4 w-4 mr-2" />
              Downgrade
            </button>
          )}

          {isActive && subscription.tier !== 'FREE' && (
            <button
              onClick={handleCancel}
              disabled={cancelling}
              className="inline-flex items-center px-4 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <XCircleIcon className="h-4 w-4 mr-2" />
              {cancelling ? 'Cancelling...' : 'Cancel Subscription'}
            </button>
          )}

          <button className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
            <CreditCardIcon className="h-4 w-4 mr-2" />
            Manage Billing
          </button>
        </div>
      </div>
    </motion.div>
  );
}
